import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { a as createLogger, e as emitEvent, l as logDeprecatedEventOnce, z as renderHiddenInputField } from './utils.js';
import { d as defineCustomElement$2 } from './cl-icon2.js';
import { d as defineCustomElement$1 } from './cl-typography2.js';

// Constants
const component = 'cl-checkbox';

const clCheckboxCss = ".sc-cl-checkbox-h{display:block}.sc-cl-checkbox-h .checkbox.sc-cl-checkbox{align-items:center;color:rgba(var(--color-neutral-900));cursor:pointer;display:flex;flex-direction:row;justify-content:space-between;-webkit-user-select:none;-moz-user-select:none;user-select:none;width:-moz-fit-content;width:fit-content}.sc-cl-checkbox-h .checkbox.sc-cl-checkbox:hover .checkbox__button.sc-cl-checkbox{border-color:rgba(var(--color-brand-600), var(--opacity-4))}.sc-cl-checkbox-h .checkbox--small.sc-cl-checkbox .checkbox__button.sc-cl-checkbox{height:var(--size-2);width:var(--size-2)}.sc-cl-checkbox-h .checkbox--small.sc-cl-checkbox .checkbox__labels_container.sc-cl-checkbox{display:block}.sc-cl-checkbox-h .checkbox--checked.sc-cl-checkbox .checkbox__button.sc-cl-checkbox{background:rgba(var(--color-brand-main));border-color:rgba(var(--color-brand-main));stroke-dashoffset:0;transition:stroke-dashoffset 90ms linear 90ms}.sc-cl-checkbox-h .checkbox--checked.sc-cl-checkbox:hover .checkbox__button.sc-cl-checkbox{border-color:rgba(var(--color-brand-main))}.sc-cl-checkbox-h .checkbox--disabled.sc-cl-checkbox{pointer-events:none}.sc-cl-checkbox-h .checkbox--disabled.sc-cl-checkbox .checkbox__button.sc-cl-checkbox{border-color:rgba(var(--color-neutral-900, 20, 20, 20), var(--opacity-3, 0.5));color:rgba(var(--color-neutral-800), var(--opacity-3, 0.5))}.sc-cl-checkbox-h .checkbox--disabled.checkbox--checked.sc-cl-checkbox .checkbox__button.sc-cl-checkbox{background-color:rgba(var(--color-neutral-900, 20, 20, 20), var(--opacity-3, 0.5));border-color:transparent}.sc-cl-checkbox-h .checkbox--error.sc-cl-checkbox:not(.checkbox--disabled) .checkbox__button.sc-cl-checkbox{border-color:rgba(var(--system-color-error-main))}.sc-cl-checkbox-h .checkbox--error.sc-cl-checkbox:not(.checkbox--disabled).checkbox--checked .checkbox__button.sc-cl-checkbox{background:rgba(var(--system-color-error-main));border-color:rgba(var(--system-color-error-main));stroke-dashoffset:0;transition:stroke-dashoffset 90ms linear 90ms}.sc-cl-checkbox-h .checkbox--error.sc-cl-checkbox:not(.checkbox--disabled):hover .checkbox__button.sc-cl-checkbox{border-color:rgba(var(--system-color-error-main))}.sc-cl-checkbox-h .checkbox__button.sc-cl-checkbox{-webkit-appearance:none;-moz-appearance:none;appearance:none;background:rgba(var(--surface-color-main));border:var(--border-width) solid rgba(var(--color-brand-600));border-radius:24.4%;box-sizing:border-box;cursor:pointer;display:flex;height:var(--spacing-3);outline:none;padding:0;position:relative;transition:background 200ms cubic-bezier(0.4, 0, 0.2, 1);width:var(--spacing-3)}.sc-cl-checkbox-h .checkbox__button__icon.sc-cl-checkbox{display:inline-flex;margin-left:calc(-1 * var(--border-width));margin-top:calc(-1 * var(--border-width))}.sc-cl-checkbox-h .checkbox__button.sc-cl-checkbox::-moz-focus-inner{border:0}.sc-cl-checkbox-h .checkbox__labels_container.sc-cl-checkbox{cursor:pointer;display:flex;flex-direction:column;justify-content:center;margin:0;margin-left:var(--spacing-1)}";

let countIds = 0;
const ClCheckbox = /*@__PURE__*/ proxyCustomElement(class ClCheckbox extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clBlur = createEvent(this, "clBlur", 7);
    this.clFocus = createEvent(this, "clFocus", 7);
    this.clToggle = createEvent(this, "clToggle", 7);
    this.blur = createEvent(this, "blurEvent", 7);
    this.focus = createEvent(this, "focusEvent", 7);
    this.toggle = createEvent(this, "toggle", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    this.inputIdentifier = `cl-checkbox-${(countIds += 1)}`;
    /**
     * Emits an event when the component has lost focus.
     */
    this.onBlur = (event) => {
      const isChecked = this.checked && !this.indeterminate;
      emitEvent(this, 'clBlur', {
        checked: isChecked,
        component,
        disabled: this.disabled,
        error: this.error,
        indeterminate: this.indeterminate,
        value: this.value,
        originalEvent: event,
      }, null);
    };
    /**
     * Updates the checked state when clicked.
     */
    this.onClick = () => {
      this.checked = !this.checked;
      if (this.indeterminate) {
        this.checked = true;
        this.indeterminate = false;
      }
    };
    /**
     * Emits an event when the component is focussed.
     */
    this.onFocus = (event) => {
      const isChecked = this.checked && !this.indeterminate;
      emitEvent(this, 'clFocus', {
        checked: isChecked,
        component,
        disabled: this.disabled,
        error: this.error,
        indeterminate: this.indeterminate,
        value: this.value,
        originalEvent: event,
      }, null);
    };
    this.checked = false;
    this.error = false;
    this.indeterminate = false;
    this.label = '';
    this.disabled = false;
    this.name = '';
    this.small = false;
    this.value = 'on';
  }
  /**
   * Handles emitting the toggle when the checked property has changed
   */
  onCheckedChanged(checked) {
    const isChecked = checked && !this.indeterminate;
    emitEvent(this, 'clToggle', {
      checked: isChecked,
      component,
      disabled: this.disabled,
      error: this.error,
      indeterminate: this.indeterminate,
      value: this.value,
    }, {
      checked: isChecked,
      value: this.value,
    });
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'blurEvent', 'clBlur');
    logDeprecatedEventOnce(this.logger, 'focusEvent', 'clFocus');
    logDeprecatedEventOnce(this.logger, 'toggle', 'clToggle');
  }
  render() {
    const { checked, disabled, error, indeterminate, label, name, small, value } = this;
    const iconName = indeterminate ? 'action--remove--alt' : 'action--checkmark';
    const notChecked = !indeterminate && !checked;
    renderHiddenInputField(name || '', this.inputIdentifier, notChecked ? value : '', this.element, true, disabled);
    return (h("div", { "aria-checked": indeterminate ? 'mixed' : checked, "aria-disabled": disabled, "aria-labelledby": this.inputIdentifier, class: cl('checkbox', {
        'checkbox--checked': !notChecked,
        'checkbox--disabled': disabled,
        'checkbox--error': error,
        'checkbox--small': small,
      }), role: 'checkbox' }, h("button", { class: 'checkbox__button', disabled: disabled, type: 'button', onBlur: this.onBlur, onClick: this.onClick, onFocus: this.onFocus }, !notChecked && (h("cl-icon", { class: 'checkbox__button__icon', fillColor: 'rgba(var(--color-neutral-50))', name: iconName, size: small ? 2 : 3 }))), label && (h("div", { class: 'checkbox__labels_container' }, h("cl-typography", Object.assign({}, (disabled && { type: 'disabled' }), { onClick: this.onClick }), label)))));
  }
  get element() { return this; }
  static get watchers() { return {
    "checked": ["onCheckedChanged"]
  }; }
  static get style() { return clCheckboxCss; }
}, [2, "cl-checkbox", {
    "checked": [1540],
    "error": [4],
    "indeterminate": [1028],
    "label": [1],
    "disabled": [4],
    "name": [1],
    "small": [4],
    "value": [1]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-checkbox", "cl-icon", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-checkbox":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClCheckbox);
      }
      break;
    case "cl-icon":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { ClCheckbox as C, defineCustomElement as d };

//# sourceMappingURL=cl-checkbox2.js.map